<template>
  <el-dialog
    v-if="dialogVisible"
    ref="dialog"
    v-loading="!allowAbort && pending"
    el-loading-background="fixed-white"
    el-loading-opacity=".8"
    el-loading-size="32"
    class="common-confirm v-confirm"
    append-to-body
    :close-on-click-modal="!allowAbort"
    :close-on-press-escape="!allowAbort"
    :data-testid="testID"
    :show-close="showClose"
    :title="title"
    :visible="dialogVisible"
    :width-size="widthSize"
    @close="onCancel"
    @keyup.enter.native.stop="onConfirm"
  >
    <div
      v-if="content || $slots['confirm-items'] || $slots.default"
      class="ma-24"
    >
      <slot :content="content">
        {{ content }}
      </slot>
      <slot name="confirm-items" />
    </div>

    <el-alert
      v-if="notice || $slots.notice"
      card
      class="ma-24"
      hide-close
      type="info"
    >
      <slot name="notice">
        {{ notice }}
      </slot>
    </el-alert>

    <template v-if="hasWarningPropItems">
      <el-alert
        v-for="(item, index) in warningPropItems"
        :key="index"
        class="ma-24"
        card
        hide-close
        type="warning"
      >
        <slot name="warning">
          {{ item }}
        </slot>
      </el-alert>
    </template>
    <template v-else-if="$slots.warning">
      <el-alert
        class="ma-24"
        card
        hide-close
        type="warning"
      >
        <slot name="warning" />
      </el-alert>
    </template>

    <error-info
      v-if="errorMessage"
      :error-message="errorMessage"
      :error-details="errorDetails"
    >
      <slot
        v-if="$slots.error"
        name="error"
      >
        {{ errorMessage }}
      </slot>
      <template #error-details>
        <slot name="error-details" />
      </template>
    </error-info>
    <template
      v-if="!hideFooter"
      #footer
    >
      <el-button
        v-if="!hideCancel"
        data-testid="cancel"
        :disabled="!mayAbortAction && pending"
        :loading="abortPending"
        type="secondary"
        @click="onCancel"
      >
        {{ buttonCancel || $t('Cancel') }}
      </el-button>
      <el-button
        :data-operationid="operationId"
        data-testid="confirm"
        :disabled="isConfirmDisabled || abortPending || (disableOnError && (!!errorMessage || !!$slots.error))"
        :loading="pending && allowAbort"
        loading-color="fixed-white"
        :type="type"
        @click="onConfirm"
      >
        {{ buttonConfirm || $t('Remove') }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts">
  import { defineComponent } from 'vue';
  import { castArray } from 'lodash-es';

  import CancellationError from '@/common/utils/errors/CancellationError';
  import CommonError from '@/common/utils/errors/CommonError';
  import ErrorInfo from '@/common/shared/ErrorInfo.vue';
  import { registerDialogEscape, unregisterDialogEscape } from '@/common/utils/dialogEscape';

  const DEFAULT_TYPE = 'danger';
  const DEFAULT_NOTICE_ICON = 'i-info-circle-small--16';
  const DEFAULT_NOTICE_ICON_COLOR = 'fixed-light';
  const DEFAULT_WIDTH_SIZE = 'small';

  export default defineComponent({
    name: 'Confirm',
    components: { ErrorInfo },
    props: {
      allowAbort: Boolean,
      hideFooter: Boolean,
      showClose: {
        type: Boolean,
        default: true
      },
      disableOnError: {
        type: Boolean,
        default: true
      },
      buttonConfirmDisabled: {
        type: Boolean,
        default: false
      },
      errorDetails: [Object, Array, String],
    },
    data() {
      return {
        type: DEFAULT_TYPE,
        hideCancel: false,
        buttonCancel: '' as string | undefined,
        buttonConfirm: '' as string | undefined,
        dialogVisible: false,
        abortAction: null as AbortAction | undefined,
        errorMessage: '',
        title: '',
        content: '' as string | object | undefined,
        widthSize: DEFAULT_WIDTH_SIZE,
        noticeIcon: DEFAULT_NOTICE_ICON,
        noticeIconColor: DEFAULT_NOTICE_ICON_COLOR,
        notice: '',
        warning: '' as string | string[] | undefined,
        pending: false,
        abortPending: false,
        operationId: undefined as string | undefined,
        testID: undefined as string | undefined,
        onReject: null as OnReject<CancellationError>,
        onResolve: null as OnResolve,
        errorDetailsVisible: false,
        disableSubmitButton: false
      };
    },
    beforeDestroy() {
      unregisterDialogEscape(this);
      this.destroy();
    },
    computed: {
      isConfirmDisabled(): boolean {
        return this.disableSubmitButton || this.buttonConfirmDisabled;
      },
      warningPropItems(): (string | undefined)[] {
        return castArray(this.warning).filter(Boolean);
      },
      hasWarningPropItems(): boolean {
        return this.warningPropItems.length > 0;
      },
      mayAbortAction(): boolean {
        return typeof this.abortAction === 'function' && this.allowAbort;
      }
    },
    watch: {
      mayAbortAction: {
        immediate: true,
        handler() {
          this.restoreAbortState();
        }
      },
      dialogVisible(value: boolean) {
        if (value) {
          registerDialogEscape(this);
        } else {
          unregisterDialogEscape(this);
        }
      }
    },
    methods: {
      triggerAbort(): Promise<any> {
        this.abortPending = true;
        this.pending = false;

        return (this.abortAction as () => Promise<any>)()
          .then((res) => {
            this.abortPending = false;
            this.destroy();
            return res;
          })
          .catch((res) => {
            this.abortPending = false;
            this.errorMessage = res.message;
            throw res;
          });
      },
      onCancel(): void {
        if (this.pending && this.mayAbortAction) {
          this.triggerAbort();
          return;
        } else if (!this.onReject || this.pending) {
          return;
        }

        this.onReject(new CancellationError());
        this.destroy();
      },
      onConfirm(): void {
        if (!this.onResolve || this.isConfirmDisabled) {
          return;
        }
        this.pending = true;
        this.onResolve();
      },
      restoreAbortState(): void {
        if (this.mayAbortAction && !this.abortPending) {
          this.pending = true;
        }
      },
      create(config: ConfirmConfig): Promise<any> {
        const { isAsync = false, title, content, action, abortAction, buttonCancel, buttonConfirm, hideCancel = false, type, widthSize, testID, ...rest } = config;
        if (typeof action !== 'function') {
          throw new CommonError('property `action` must be a function');
        }
        this.widthSize = widthSize || DEFAULT_WIDTH_SIZE;
        this.type = type || DEFAULT_TYPE;
        this.title = title;
        this.content = content;
        this.buttonCancel = buttonCancel;
        this.buttonConfirm = buttonConfirm;
        this.hideCancel = hideCancel;
        this.abortAction = abortAction;
        this.operationId = rest.operationId;
        this.notice = rest.notice || this.notice;
        this.noticeIcon = rest.noticeIcon || this.noticeIcon;
        this.noticeIconColor = rest.noticeIconColor || this.noticeIconColor;
        this.warning = rest.warning || this.warning;
        this.disableSubmitButton = rest.disableSubmitButton || this.disableSubmitButton;

        this.errorMessage = rest.errorMessage || '';
        this.pending = false;
        this.dialogVisible = true;

        this.testID = testID;

        this.restoreAbortState();

        this.$emit('open');
        return new Promise((resolve, reject) => {
          this.onResolve = resolve;
          this.onReject = reject;
        })
          .then((res) => {
            if (!isAsync) {
              return action();
            }

            action();
            return res;
          })
          .then((res) => {
            if (!this.abortAction) {
              this.destroy();
            }
            return res;
          })
          .catch((err) => {
            this.pending = false;

            if (err instanceof CancellationError) {
              throw err;
            }

            if (err instanceof Error) {
              this.errorMessage = err.message;

              if (this.disableOnError) {
                throw err;
              }
            }

            return this.create({ ...config, errorMessage: this.errorMessage });
          });
      },
      destroy(): void {
        this.dialogVisible = false;
        this.widthSize = DEFAULT_WIDTH_SIZE;
        this.type = DEFAULT_TYPE;
        this.title = '';
        this.content = '';
        this.errorMessage = '';
        this.buttonCancel = '';
        this.buttonConfirm = '';
        this.hideCancel = false;
        this.disableSubmitButton = false;

        this.notice = '';
        this.noticeIcon = DEFAULT_NOTICE_ICON;
        this.noticeIconColor = DEFAULT_NOTICE_ICON_COLOR;

        this.warning = '';

        this.abortAction = null;
        this.onResolve = null;
        this.onReject = null;
        this.pending = false;
        this.abortPending = false;

        this.operationId = undefined;
        this.testID = undefined;
        this.errorDetailsVisible = false;
      }
    }
  });
</script>

<style scoped lang="scss">
  .details-button {
    margin: 4px 0 8px -8px;
  }

  .error-details {
    max-height: 264px;
    border-radius: $av-border-radius-2;
    background-color: $av-inversed-primary;
    border: $av-border-sm $av-brand-lighter;
  }
</style>
